import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, athleteAuthAPI, athleteProfilesAPI } from '../lib/api';

const AuthContext = createContext(null);

const FREE_ATHLETE_LIMIT = 10;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  // Lista profili atleta quando una stessa email ha più figli — null se non applicabile
  const [athleteProfiles, setAthleteProfiles] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUserType = localStorage.getItem('userType');

      if (token && storedUserType) {
        try {
          if (storedUserType === 'coach') {
            const response = await authAPI.me();
            setUser(response.data);
            setUserType('coach');
          } else if (storedUserType === 'athlete') {
            const response = await athleteAuthAPI.me();
            setUser(response.data);
            setUserType('athlete');
          }
        } catch (error) {
          // Pulisci sia localStorage che stato React su qualsiasi errore di auth
          localStorage.removeItem('token');
          localStorage.removeItem('userType');
          localStorage.removeItem('hasDualRole');
          setUser(null);
          setUserType(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const refreshCoachData = async () => {
    try {
      const response = await authAPI.me();
      setUser(response.data);
    } catch (error) {
      // ignora
    }
  };

  const isOverLimit =
    userType === 'coach' &&
    user?.subscription_plan === 'free' &&
    typeof user?.athlete_count === 'number' &&
    user.athlete_count > FREE_ATHLETE_LIMIT;

  const loginCoach = async (email, password) => {
    const response = await authAPI.login({ email, password });
    // Prima salva in localStorage
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('userType', 'coach');
    // Poi aggiorna lo stato React (questo triggererà il re-render)
    setUser(response.data.coach);
    setUserType('coach');
    // Delay più lungo per iOS/Capacitor per assicurare sincronizzazione completa
    await new Promise(resolve => setTimeout(resolve, 150));
    return response.data;
  };

  const registerCoach = async (name, email, password, hourlyRate = 0, variableRates = []) => {
    const formattedRates = variableRates.map(r => ({
      min_athletes: parseInt(r.min_athletes) || 0,
      max_athletes: parseInt(r.max_athletes) || 0,
      rate_per_hour: parseFloat(r.rate_per_hour) || 0
    }));
    const response = await authAPI.register({ 
      name, 
      email, 
      password, 
      hourly_rate: hourlyRate,
      variable_rates: formattedRates
    });
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('userType', 'coach');
    setUser(response.data.coach);
    setUserType('coach');
    return response.data;
  };

  const registerDual = async (name, email, password, hourlyRate = 0, variableRates = []) => {
    const formattedRates = variableRates.map(r => ({
      min_athletes: parseInt(r.min_athletes) || 0,
      max_athletes: parseInt(r.max_athletes) || 0,
      rate_per_hour: parseFloat(r.rate_per_hour) || 0
    }));
    const response = await authAPI.registerDual({ 
      name, 
      email, 
      password, 
      hourly_rate: hourlyRate,
      variable_rates: formattedRates
    });
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('userType', 'coach');
    localStorage.setItem('hasDualRole', 'true');
    setUser(response.data.coach);
    setUserType('coach');
    return response.data;
  };

  const loginAthlete = async (email, password) => {
    const response = await athleteAuthAPI.login({ email, password });
    if (response.data.profiles && response.data.profiles.length > 1) {
      // Più profili: non logga ancora, restituisce la lista per la selezione
      setAthleteProfiles({ profiles: response.data.profiles, email, password });
      return response.data;
    }
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('userType', 'athlete');
    setUser(response.data.athlete);
    setUserType('athlete');
    setAthleteProfiles(null);
    await new Promise(resolve => setTimeout(resolve, 50));
    return response.data;
  };

  const selectAthleteProfile = async (targetAthleteId) => {
    if (!athleteProfiles) return;
    const res = await athleteProfilesAPI.loginAsProfile(athleteProfiles.email, athleteProfiles.password, targetAthleteId);
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('userType', 'athlete');
    setUser(res.data.athlete);
    setUserType('athlete');
    setAthleteProfiles(null);
    await new Promise(resolve => setTimeout(resolve, 50));
    return res.data.athlete;
  };

  const switchAthleteProfile = async (targetAthleteId) => {
    const response = await athleteProfilesAPI.switchProfile(targetAthleteId);
    localStorage.setItem('token', response.data.token);
    setUser(response.data.athlete);
    await new Promise(resolve => setTimeout(resolve, 50));
    return response.data.athlete;
  };

  const registerAthlete = async (name, email, password) => {
    const response = await athleteAuthAPI.register({ name, email, password });
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('userType', 'athlete');
    setUser(response.data.athlete);
    setUserType('athlete');
    return response.data;
  };

  const registerAthleteWithInvite = async (name, email, password, inviteToken) => {
    const response = await athleteAuthAPI.registerWithInvite({ name, email, password }, inviteToken);
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('userType', 'athlete');
    setUser(response.data.athlete);
    setUserType('athlete');
    return response.data;
  };

  const switchToAthlete = async () => {
    try {
      const response = await authAPI.switchToAthlete();
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userType', 'athlete');
      setUser(response.data.athlete);
      setUserType('athlete');
      window.location.href = '/athlete/dashboard';
    } catch (error) {
      console.error('Error switching to athlete', error);
      throw error;
    }
  };

  const switchToCoach = async () => {
    try {
      const response = await authAPI.switchToCoach();
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userType', 'coach');
      setUser(response.data.coach);
      setUserType('coach');
      window.location.href = '/lessons';
    } catch (error) {
      console.error('Error switching to coach', error);
      throw error;
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const deleteAccount = async () => {
    try {
      await authAPI.deleteAccount();
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      localStorage.removeItem('hasDualRole');
      setUser(null);
      setUserType(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('hasDualRole');
    setUser(null);
    setUserType(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user,
      userType,
      loading,
      loginCoach,
      registerCoach,
      registerDual,
      loginAthlete,
      registerAthlete,
      registerAthleteWithInvite,
      switchToAthlete,
      switchToCoach,
      updateUser,
      deleteAccount,
      logout,
      refreshCoachData,
      isOverLimit,
      athleteProfiles,
      selectAthleteProfile,
      switchAthleteProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
